/**
 * Breakpoints layout — source unique pour JS/TS et CSS (--layout-breakpoint-*).
 * Les media queries CSS doivent rester alignées sur ces valeurs.
 */
export const BREAKPOINTS = {
  /** Très petits téléphones */
  xs: 480,
  /** Mobile ↔ tablette / desktop (seuil principal de l'app) */
  sm: 768,
  /** Tablette ↔ desktop large */
  md: 1024,
  /** Desktop large */
  lg: 1280,
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

/** Surface produit : mobile (app-like) vs desktop (discovery / SEO). */
export type AppLayoutMode = 'mobile' | 'desktop';

/** Granularité viewport pour affinage futur (pilotes UI). */
export type AppLayoutSurface = 'mobile' | 'tablet' | 'desktop';

export function getBreakpointKey(width: number): BreakpointKey {
  if (width < BREAKPOINTS.xs) return 'xs';
  if (width < BREAKPOINTS.sm) return 'xs';
  if (width < BREAKPOINTS.md) return 'sm';
  if (width < BREAKPOINTS.lg) return 'md';
  return 'lg';
}

export function getLayoutSurface(width: number): AppLayoutSurface {
  if (width < BREAKPOINTS.sm) return 'mobile';
  if (width < BREAKPOINTS.md) return 'tablet';
  return 'desktop';
}

/** Seuil principal : < sm = layout mobile (comportement actuel de l'app). */
export function isMobileViewport(width: number): boolean {
  return width < BREAKPOINTS.sm;
}

export function isTabletViewport(width: number): boolean {
  return width >= BREAKPOINTS.sm && width < BREAKPOINTS.md;
}

export function isDesktopViewport(width: number): boolean {
  return width >= BREAKPOINTS.md;
}

export function resolveAppLayoutMode(width: number, isNative: boolean): AppLayoutMode {
  if (isNative) return 'mobile';
  return isMobileViewport(width) ? 'mobile' : 'desktop';
}

/** Chaînes media query réutilisables côté JS (matchMedia, tests). */
export const MEDIA_QUERIES = {
  maxXs: `(max-width: ${BREAKPOINTS.xs - 1}px)`,
  maxSm: `(max-width: ${BREAKPOINTS.sm - 1}px)`,
  minSm: `(min-width: ${BREAKPOINTS.sm}px)`,
  maxMd: `(max-width: ${BREAKPOINTS.md - 1}px)`,
  minMd: `(min-width: ${BREAKPOINTS.md}px)`,
  minLg: `(min-width: ${BREAKPOINTS.lg}px)`,
} as const;
